import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/** `GET /controlling/strip` (docs/dev/api-tasks.md — Controlling strip). */

export type StripScope = 'me' | 'team' | 'organization';
export type StripPeriod = 'week' | 'month' | 'quarter';

export interface StripKpi {
  key: 'calls' | 'appointments' | 'attended_rate' | 'deals_new' | 'notarized';
  label: string;
  value: number | null;
  valueLabel: string;
  delta: { value: number; label: string; tone: 'positive' | 'negative' | 'neutral' } | null;
  extra: string | null;
}

export interface StripResponse {
  ready: boolean;
  reason: string | null;
  callsSoFar: number;
  period: { key: StripPeriod; label: string; from: string; to: string };
  scope: {
    key: StripScope;
    label: string;
    options: Array<{ key: StripScope; label: string; available: boolean }>;
  };
  kpis: StripKpi[];
  detailsHref: string;
}

export const stripKey = (scope: StripScope, period: StripPeriod) =>
  [...queryRoots.controlling, 'strip', scope, period] as const;

/** The strip's figures; a 403 (permission `controlling.figures` = none) is not retried and hides the strip. */
export function useControllingStrip(scope: StripScope, period: StripPeriod) {
  return useQuery({
    queryKey: stripKey(scope, period),
    queryFn: () => api<StripResponse>(`/controlling/strip?scope=${scope}&period=${period}`),
    retry: (count, error) => !(error instanceof ApiError && error.status === 403) && count < 1,
    staleTime: 60_000,
  });
}

export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}
